import '@/polyfills-loader';
import { useMemo } from 'preact/hooks';
import type { Product } from '@/domain/Product.ts';
import type { WidgetConfig } from '@/domain/WidgetConfig.ts';
import type { Market } from '@/domain/Market.ts';
import Providers from './Providers.tsx';
import ProductListLayout from './ProductListLayout/ProductListLayout.tsx';
import type { Categories } from '@/domain/Category.ts';
import {
  defaultProductTranslations,
  type ProductTranslations
} from '@/domain/config/index.ts';
import createI18nInstance from '@/utils/createI18nInstance.ts';
import useProductWithRules from './hooks/useProductWithRules.tsx';
import type { CustomerProductRules } from '@/domain/assemblers/ProductAssembler.ts';
import type { UseRoute } from '../hooks/useRoute.tsx';

export type ProductListProps = {
  route: UseRoute;
  products: Product[];
  categories: Categories;
  config: WidgetConfig;
  marketConfig: Market;
  productTranslations?: ProductTranslations;
  // Note: The `customerProductRules` parameter is only provided when using
  // the widget loader. For static site generation (SSG) where the loader isn't
  // used, preferences will be fetched internally by the `useHydrateProducts` hook.
  customerProductRules?: CustomerProductRules;
};

export default function ProductList({
  route,
  products,
  categories,
  config,
  marketConfig,
  productTranslations,
  customerProductRules
}: ProductListProps) {
  const productsWithPreferences = useProductWithRules(
    products,
    route,
    customerProductRules
  );
  const productI18nInstance = useMemo(
    () =>
      createI18nInstance<ProductTranslations>(
        defaultProductTranslations,
        productTranslations,
        route.locale
      ),
    [productTranslations, route.locale]
  );

  return (
    <Providers config={config} marketConfig={marketConfig} route={route}>
      <ProductListLayout
        products={productsWithPreferences}
        categories={categories}
        productI18nInstance={productI18nInstance}
      />
    </Providers>
  );
}
